// My agent › Mail and calendar (M4-04; services/api/src/domain/connections/crew.ts): the person
// lets their own agent read their mail or their calendar, each through one of their OWN Google
// connections (a crew is a subset of its owner). Read when opened ("Choose mail and calendar"),
// so the screen keeps its load to crews.me and the reminders. Each read says why it is closed:
// the company's switch is off (Setup › Keys & connections), no connection of yours can serve it
// (My subscriptions › Connections), Gmail read waits on Google's security review (a founder
// input), or the connection chosen needs attention (reconnect it). The set is one PUT.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SaveIndicator,
  SettingsSection,
  Skeleton,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { MailIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { HEALTH } from "../subscriptions/copy";

type View = OperationOutput<"crews.connections">;
type Access = View["mail"];
type Kind = "mail" | "calendar";

export const crewConnectionsKey = (co: string) => ["company", co, "crews", "connections"] as const;
export const crewConnectionsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: crewConnectionsKey(co),
    queryFn: () => api.client.crews.connections({ params: { co } }),
  });

const LABEL: Record<Kind, { title: string; reads: string }> = {
  calendar: { title: "Calendar", reads: "your calendar events (read only)" },
  mail: { title: "Mail", reads: "who wrote to you, subjects and messages (read only)" },
};

function blockedText(a: Access, slug: string): React.ReactNode {
  switch (a.blocked) {
    case "flag_off":
      return "Off in this company: an owner or admin turns on crew mail and calendar in Team › Connections › Paths.";
    case "founder_gated":
      return "Gmail read waits on Google's security review before CompanyOS may offer it.";
    case "no_connection":
      return (
        <>
          None of your connections can serve this yet.{" "}
          <Link to="/c/$slug/subscriptions" params={{ slug }} search={{ tab: "connections" }}>
            Connect Google
          </Link>{" "}
          with {a.kind === "mail" ? "Gmail read" : "Calendar read"} first.
        </>
      );
    case "connection_unusable":
      return (
        <>
          The connection chosen needs attention.{" "}
          <Link to="/c/$slug/subscriptions" params={{ slug }} search={{ tab: "connections" }}>
            Reconnect or check it
          </Link>
          ; your agent cannot read until then.
        </>
      );
    default:
      return null;
  }
}

export function MailCalendarSection({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const [open, setOpen] = useState(false);
  const view = useQuery({ ...crewConnectionsQuery(api, co), enabled: open });
  return (
    <SettingsSection
      id="mail-calendar"
      icon={MailIcon}
      title="Mail and calendar"
      description="Whether your agent may read your mail and your calendar, through your own connection."
      actions={
        open ? null : (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Choose mail and calendar
          </Button>
        )
      }
    >
      <p className="text-sm text-muted-foreground" data-slot="mail-calendar-explainer">
        Off unless you choose a connection. Your agent only reads; it never sends or changes
        anything, and it never reads through anyone else's connection.
      </p>
      {!open ? null : view.isPending ? (
        <div role="status" aria-label="Loading mail and calendar" className="flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : view.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(view.error, "Mail and calendar could not be loaded.")}
        </Notice>
      ) : (
        <AccessForm
          key={`${view.data.mail.grant?.connection_id ?? ""}:${view.data.calendar.grant?.connection_id ?? ""}`}
          api={api}
          company={company}
          view={view.data}
        />
      )}
    </SettingsSection>
  );
}

function AccessForm({ api, company, view }: { api: Api; company: CompanyAccess; view: View }) {
  const qc = useQueryClient();
  const co = company.id;
  const saved: Record<Kind, string> = {
    mail: view.mail.grant?.connection_id ?? "",
    calendar: view.calendar.grant?.connection_id ?? "",
  };
  const [picked, setPicked] = useState(saved);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: (p: Record<Kind, string>) =>
      api.client.crews.putConnections({
        params: { co },
        body: { mail: p.mail || null, calendar: p.calendar || null },
      }),
    onSuccess: (v) => {
      qc.setQueryData(crewConnectionsKey(co), v);
      setSavedAt(Date.now());
    },
  });
  const dirty = picked.mail !== saved.mail || picked.calendar !== saved.calendar;
  return (
    <form
      aria-label="Mail and calendar"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(picked);
      }}
    >
      {(["calendar", "mail"] as const).map((k) => (
        <AccessRow
          key={k}
          access={view[k]}
          slug={company.slug}
          value={picked[k]}
          onChange={(v) => setPicked((x) => ({ ...x, [k]: v }))}
        />
      ))}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save mail and calendar"}
        </Button>
        <SaveIndicator savedAt={savedAt} />
      </div>
      {save.error ? (
        <Notice tone="danger" role="alert">
          {problemText(save.error, "Mail and calendar could not be saved.")}
        </Notice>
      ) : null}
    </form>
  );
}

function AccessRow({
  access: a,
  slug,
  value,
  onChange,
}: {
  access: Access;
  slug: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  const k = a.kind as Kind;
  // A read that is off and cannot be turned on offers only "Off"; one granted keeps its choice.
  const closed = !a.grant && (a.blocked === "flag_off" || a.blocked === "founder_gated");
  return (
    <Field data-access={k}>
      <FieldLabel htmlFor={id} className="flex items-center gap-2">
        {LABEL[k].title}
        <Badge variant={a.grant ? (a.blocked ? "warning" : "success") : "outline"}>
          {a.grant ? (a.blocked ? "Needs attention" : "On") : "Off"}
        </Badge>
      </FieldLabel>
      <NativeSelect
        id={id}
        className="w-full"
        value={value}
        disabled={closed}
        onChange={(e) => onChange(e.target.value)}
      >
        <NativeSelectOption value="">Off</NativeSelectOption>
        {a.eligible.map((c) => (
          <NativeSelectOption key={c.connection_id} value={c.connection_id}>
            {c.label}
            {c.identity_display ? ` (${c.identity_display})` : ""}
            {c.state === "healthy" ? "" : ` · ${HEALTH[c.state]?.label ?? c.state}`}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <FieldDescription>
        {a.blocked ? blockedText(a, slug) : `Your agent may read ${LABEL[k].reads}.`}
      </FieldDescription>
    </Field>
  );
}
